import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { formatMoney } from '../mirror/time';
import { centsFromInput } from '../money/input';
import { useLocale, useT } from '../messages';

/**
 * Account credit, on the guest's own record (module `credit`, §7.4 rule 2).
 *
 * > `cash` credit is real money — income when added, a payment when spent.
 * > `gift` credit is goodwill — no income when added, a discount when spent.
 * > Collapsing the two overstates revenue.
 *
 * That sentence is the whole screen, and it is why there is no single "balance"
 * anywhere on it. Two pools, side by side, never summed — because they are
 * different liabilities: one is money the guest handed over, the other is a
 * promise the owner made. A shop deciding whether it can afford something needs
 * to know which is which at exactly that moment.
 *
 * The buttons are labelled as the accounting rather than as the gesture:
 * *money in* and *goodwill*. "Add credit" with a dropdown would let somebody pick
 * the wrong one without noticing what they had chosen.
 *
 * **Nothing here spends.** Spending happens in the waterfall at checkout, gift
 * before cash, decided by the server — a balance drawn down outside a settlement
 * would be money leaving the ledger with no document to close against.
 */
export function CreditPanel({
  clientId,
  clientName,
  currency,
}: {
  clientId: string;
  clientName: string;
  currency: string;
}): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const locale = useLocale();

  const credit = useLive(() => runtime.mirror.credit(clientId), null, [clientId]);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  /**
   * Fetch this guest's history when their record opens.
   *
   * Balances for the whole book arrive in one read at hydrate; the *history*
   * behind one balance is per client and is not worth a request per client at
   * connect time. This is the same shape as `sync.start()` in the shell: an
   * effect asks, the answer lands in the mirror, and the render path stays
   * local — offline the request fails and the panel shows the balances it
   * already has, with no spinner and nothing invented.
   */
  useEffect(() => {
    void runtime.mirror.refetchCredit(clientId).catch(() => undefined);
  }, [runtime, clientId]);

  const cents = centsFromInput(amount);
  const entries = credit?.entries ?? [];

  const add = (kind: 'cash' | 'gift'): void => {
    if (cents === null || cents <= 0) return;
    void actions.addCredit({
      clientId,
      name: clientName,
      kind,
      amountCents: cents,
      ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
    });
    setAmount('');
    setReason('');
  };

  return (
    <section className="credit-panel">
      <h2>{t('credit.heading')}</h2>
      <dl className="facts">
        <dt>{t('credit.cash')}</dt>
        <dd>
          {formatMoney(credit?.cash_cents ?? 0, currency, locale)}
          <small className="muted">{t('credit.cashHint')}</small>
        </dd>
        <dt>{t('credit.gift')}</dt>
        <dd>
          {formatMoney(credit?.gift_cents ?? 0, currency, locale)}
          <small className="muted">{t('credit.giftHint')}</small>
        </dd>
      </dl>

      <div className="editor editor--inline">
        <input
          aria-label={t('credit.amountLabel')}
          placeholder={t('credit.amountPlaceholder')}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <input
          aria-label={t('credit.reasonLabel')}
          placeholder={t('credit.reasonPlaceholder')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <button
          type="button"
          className="button"
          disabled={cents === null || cents <= 0}
          onClick={() => add('cash')}
        >
          {t('credit.moneyIn')}
        </button>
        <button
          type="button"
          className="button"
          disabled={cents === null || cents <= 0}
          onClick={() => add('gift')}
        >
          {t('credit.goodwill')}
        </button>
      </div>

      <ul className="credit-list">
        {entries.map((entry) => (
          <li key={entry.credit_entry_id}>
            <span>
              <strong>
                {entry.amount_cents < 0 ? '−' : '+'}
                {formatMoney(Math.abs(entry.amount_cents), currency, locale)}
              </strong>{' '}
              {entry.kind === 'cash' ? t('credit.kindCash') : t('credit.kindGift')}
            </span>
            <small>
              {entry.occurred_on}
              {entry.reason === '' ? '' : t('credit.reasonTail', { reason: entry.reason })}
              {entry.settlement_id === null ? '' : t('credit.spentAtCheckout')}
            </small>
          </li>
        ))}
        {entries.length === 0 ? <li className="muted">{t('credit.empty')}</li> : null}
      </ul>
    </section>
  );
}
